/**
 * Gin Rummy — server-authoritative rules engine, 2 players.
 *
 * Rules implemented (toggles live in {@link GinConfig}):
 *  - One 52-card deck, 10 cards each. The next card is turned up to start the discard pile; the rest is the stock.
 *  - Opening: the non-dealer may take the upcard or pass; if they pass the dealer may take it or pass; if both pass the
 *    non-dealer must draw from the stock. A player who takes the upcard discards a different card.
 *  - A turn is: draw from the stock or take the top discard, then discard one card (never the card just taken from the
 *    discard pile) — or knock.
 *  - Melds: runs of three or more consecutive cards of one suit (ace low only) and sets of three or four of one rank.
 *    Everything outside the melds is deadwood: ace 1, 2-10 face value, J/Q/K 10.
 *  - Knock: after drawing, a player whose hand (after the knock discard) has 10 or fewer deadwood points may knock. With
 *    no deadwood at all that is "gin". The opponent then reveals their hand; unless the knock was gin they may lay off
 *    deadwood cards onto the knocker's melds (done automatically for them).
 *  - Scoring: if the knocker's deadwood is lower, they score the difference; gin scores `ginBonus` (25) plus the
 *    opponent's deadwood; if the opponent's deadwood is equal or lower the knock is undercut and the opponent scores the
 *    difference plus `undercutBonus` (25).
 *  - When the stock is down to two cards and the player discards without knocking, the hand is a draw and nobody scores.
 *  - A match ends when a player reaches `targetScore` (or after `maxHands`). The winner adds `gameBonus` (100) and each
 *    player adds `boxBonus` (25) per hand won; final totals decide the places.
 */
import { buildDeck, removeCards, sortHand, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';
import { analyzeGinHand, ginBestDiscard, ginCardValue, ginLayoff, type GinArrangement, type GinMeld } from './melds.js';

export interface GinConfig {
  targetScore: number;
  /** Hard cap on hands so a match always terminates. */
  maxHands: number;
  /** Most deadwood a player may knock with. */
  knockMax: number;
  ginBonus: number;
  undercutBonus: number;
  gameBonus: number;
  boxBonus: number;
  /** Safety valve: a hand with this many discards and nobody knocking is a draw. */
  maxTurns: number;
  handEndDelayMs: number;
}

export type GinPhase = 'FIRST' | 'DRAW' | 'DISCARD' | 'HAND_END' | 'MATCH_END';
export type GinOutcome = 'GIN' | 'KNOCK' | 'UNDERCUT' | 'DRAW';

export interface GinHandResult {
  hand: number;
  outcome: GinOutcome;
  /** Seat that scored, or null for a drawn hand. */
  winner: number | null;
  knocker: number | null;
  /** Points added to the winner's score. */
  points: number;
  /** Each seat's final melds (the knocker's include the opponent's laid-off cards) and leftover deadwood. */
  melds: GinMeld[][];
  deadwood: CardId[][];
  laidOff: CardId[];
}

export interface GinState {
  config: GinConfig;
  players: string[];
  hand: number;
  dealer: number;
  phase: GinPhase;
  hands: CardId[][];
  stock: CardId[];
  discard: CardId[];
  turn: number;
  /** Opening offers already declined (0 or 1) while phase is FIRST. */
  offers: number;
  /** After both players pass the upcard the non-dealer must draw from the stock. */
  stockOnly: boolean;
  /** The card taken from the discard pile this turn; it cannot be thrown straight back. */
  locked: CardId | null;
  /** Cards each seat took from the discard pile this hand (public, since the pile is). */
  picked: CardId[][];
  turnsThisHand: number;
  scores: number[];
  handsWon: number[];
  /** Set when the match ends: scores plus the game and box bonuses. */
  finalScores: number[] | null;
  handResults: GinHandResult[];
}

export type GinAction =
  | { type: 'takeUpcard' }
  | { type: 'passUpcard' }
  | { type: 'draw'; from: 'stock' | 'discard' }
  | { type: 'discard'; card: CardId }
  | { type: 'knock'; card: CardId };

export interface GinLegal {
  canTakeUpcard: boolean;
  canPassUpcard: boolean;
  canDrawStock: boolean;
  canDrawDiscard: boolean;
  /** Cards that may be discarded now. */
  discardable: CardId[];
  /** Cards whose discard lets the player knock (deadwood within the limit). */
  knockable: CardId[];
}

export interface GinView {
  gameType: 'GIN_RUMMY';
  config: GinConfig;
  seat: number | null;
  phase: GinPhase;
  hand: number;
  dealer: number;
  turn: number;
  /** My cards. */
  cards: CardId[];
  handCounts: number[];
  stockCount: number;
  discardTop: CardId | null;
  discardPile: CardId[];
  /** The card just taken from the discard pile this turn, which cannot be discarded. */
  locked: CardId | null;
  picked: CardId[][];
  scores: number[];
  handsWon: number[];
  handResults: GinHandResult[];
  /** Final scores once the match is over (the same as `scores` before that). */
  totals: number[];
}

const DEFAULTS: GinConfig = {
  targetScore: 100,
  maxHands: 15,
  knockMax: 10,
  ginBonus: 25,
  undercutBonus: 25,
  gameBonus: 100,
  boxBonus: 25,
  maxTurns: 120,
  handEndDelayMs: 7000,
};

const HAND = 10;

function deal(state: GinState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck(), rng);
  state.hands = [sortHand(deck.slice(0, HAND)), sortHand(deck.slice(HAND, 2 * HAND))];
  state.discard = [deck[2 * HAND]!];
  state.stock = deck.slice(2 * HAND + 1);
  state.phase = 'FIRST';
  state.turn = (state.dealer + 1) % 2;
  state.offers = 0;
  state.stockOnly = false;
  state.locked = null;
  state.picked = [[], []];
  state.turnsThisHand = 0;
  events.push({ type: 'dealt', hand: state.hand, dealer: state.dealer, upcard: state.discard[0] });
}

/** The knock options for a 11-card hand: discards that leave at most `knockMax` deadwood. */
function knockableCards(hand: readonly CardId[], locked: CardId | null, knockMax: number): CardId[] {
  const an = analyzeGinHand(hand);
  return hand.filter((c, i) => c !== locked && an.deadwood(i) <= knockMax);
}

function endHand(state: GinState, result: GinHandResult, events: EngineEvent[]): void {
  state.handResults.push(result);
  if (result.winner !== null) {
    state.scores[result.winner]! += result.points;
    state.handsWon[result.winner]!++;
  }
  events.push({ type: 'handEnd', hand: state.hand, outcome: result.outcome, winner: result.winner, points: result.points, scores: [...state.scores] });
  const reached = state.scores.some((s) => s >= state.config.targetScore);
  if (reached || state.hand >= state.config.maxHands) {
    const cfg = state.config;
    const leader = state.scores[0]! === state.scores[1]! ? -1 : state.scores[0]! > state.scores[1]! ? 0 : 1;
    state.finalScores = state.scores.map((s, seat) => s + (seat === leader ? cfg.gameBonus : 0) + state.handsWon[seat]! * cfg.boxBonus);
    state.phase = 'MATCH_END';
    events.push({ type: 'matchEnd', standings: standingsFromScores(state.finalScores.map((score, seat) => ({ seat, score }))) });
  } else {
    state.phase = 'HAND_END';
  }
}

function drawnHand(state: GinState, events: EngineEvent[]): void {
  endHand(
    state,
    {
      hand: state.hand,
      outcome: 'DRAW',
      winner: null,
      knocker: null,
      points: 0,
      melds: [[], []],
      deadwood: state.hands.map((h) => [...h]),
      laidOff: [],
    },
    events,
  );
}

function resolveKnock(state: GinState, knocker: number, events: EngineEvent[]): void {
  const cfg = state.config;
  const other = 1 - knocker;
  const mine: GinArrangement = analyzeGinHand(state.hands[knocker]!).arrangement();
  const theirs: GinArrangement = analyzeGinHand(state.hands[other]!).arrangement();
  const gin = mine.points === 0;
  let melds: GinMeld[][] = [[], []];
  melds[knocker] = mine.melds;
  melds[other] = theirs.melds;
  let theirDead = theirs.deadwood;
  let laidOff: CardId[] = [];
  if (!gin) {
    const lay = ginLayoff(mine.melds, theirs.deadwood);
    melds[knocker] = lay.melds;
    theirDead = lay.remaining;
    laidOff = lay.laidOff;
  }
  const theirPoints = theirDead.reduce((n, c) => n + ginCardValue(c), 0);
  let outcome: GinOutcome;
  let winner: number;
  let points: number;
  if (gin) {
    outcome = 'GIN';
    winner = knocker;
    points = cfg.ginBonus + theirPoints;
  } else if (mine.points < theirPoints) {
    outcome = 'KNOCK';
    winner = knocker;
    points = theirPoints - mine.points;
  } else {
    outcome = 'UNDERCUT';
    winner = other;
    points = mine.points - theirPoints + cfg.undercutBonus;
  }
  melds = melds.map((m) => m.map((x) => ({ kind: x.kind, cards: [...x.cards] })));
  const deadwood: CardId[][] = [[], []];
  deadwood[knocker] = mine.deadwood;
  deadwood[other] = theirDead;
  events.push({ type: 'knock', seat: knocker, gin });
  endHand(state, { hand: state.hand, outcome, winner, knocker, points, melds, deadwood, laidOff }, events);
}

export const ginEngine: GameEngine<GinConfig, GinState, GinAction, GinView, GinLegal> = {
  gameType: 'GIN_RUMMY',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 20, 500);
    checkInt('maxHands', c.maxHands, 1, 40);
    checkInt('knockMax', c.knockMax, 0, 10);
    checkInt('ginBonus', c.ginBonus, 0, 100);
    checkInt('undercutBonus', c.undercutBonus, 0, 100);
    checkInt('gameBonus', c.gameBonus, 0, 500);
    checkInt('boxBonus', c.boxBonus, 0, 100);
    checkInt('maxTurns', c.maxTurns, 10, 400);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: () => ({ min: 2, max: 2 }),

  createState(config, seats, rng) {
    assertEngine(seats.length === 2, 'INVALID_CONFIG', 'Gin Rummy needs exactly 2 seats');
    const players = Array<string>(2).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < 2 && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: GinState = {
      config,
      players,
      hand: 1,
      dealer: rng.int(2),
      phase: 'FIRST',
      hands: [],
      stock: [],
      discard: [],
      turn: 0,
      offers: 0,
      stockOnly: false,
      locked: null,
      picked: [[], []],
      turnsThisHand: 0,
      scores: [0, 0],
      handsWon: [0, 0],
      finalScores: null,
      handResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'takeUpcard') return { type: 'takeUpcard' };
    if (raw.type === 'passUpcard') return { type: 'passUpcard' };
    if (raw.type === 'draw' && (raw.from === 'stock' || raw.from === 'discard')) return { type: 'draw', from: raw.from };
    if (raw.type === 'discard' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'discard', card: raw.card };
    if (raw.type === 'knock' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'knock', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Gin Rummy action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(state.phase === 'FIRST' || state.phase === 'DRAW' || state.phase === 'DISCARD', 'INVALID_PHASE', 'No action is expected now');
    assertEngine(seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;

    if (action.type === 'takeUpcard' || action.type === 'passUpcard') {
      assertEngine(state.phase === 'FIRST', 'INVALID_PHASE', 'The upcard is no longer on offer');
      if (action.type === 'takeUpcard') {
        const card = state.discard.pop()!;
        hand.push(card);
        state.hands[seat] = sortHand(hand);
        state.picked[seat]!.push(card);
        state.locked = card;
        state.phase = 'DISCARD';
        events.push({ type: 'takeUpcard', seat, card });
        return events;
      }
      events.push({ type: 'passUpcard', seat });
      state.offers++;
      if (state.offers === 1) {
        state.turn = state.dealer;
      } else {
        state.phase = 'DRAW';
        state.turn = (state.dealer + 1) % 2;
        state.stockOnly = true;
      }
      return events;
    }

    if (action.type === 'draw') {
      assertEngine(state.phase === 'DRAW', 'INVALID_PHASE', 'You have already drawn');
      if (action.from === 'discard') {
        assertEngine(!state.stockOnly, 'ILLEGAL_MOVE', 'Both players passed the upcard: draw from the stock');
        const card = state.discard.pop()!;
        hand.push(card);
        state.hands[seat] = sortHand(hand);
        state.picked[seat]!.push(card);
        state.locked = card;
        events.push({ type: 'draw', seat, from: 'discard', card });
      } else {
        const card = state.stock.pop()!;
        hand.push(card);
        state.hands[seat] = sortHand(hand);
        state.locked = null;
        events.push({ type: 'draw', seat, from: 'stock' });
      }
      state.stockOnly = false;
      state.phase = 'DISCARD';
      return events;
    }

    assertEngine(state.phase === 'DISCARD', 'INVALID_PHASE', 'Draw a card first');
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(action.card !== state.locked, 'ILLEGAL_MOVE', 'You cannot discard the card you just took');

    if (action.type === 'knock') {
      const rest = hand.filter((c) => c !== action.card);
      assertEngine(analyzeGinHand(rest).deadwood() <= state.config.knockMax, 'ILLEGAL_MOVE', 'Too much deadwood to knock');
      removeCards(hand, [action.card]);
      state.discard.push(action.card);
      resolveKnock(state, seat, events);
      return events;
    }

    removeCards(hand, [action.card]);
    state.discard.push(action.card);
    state.turnsThisHand++;
    events.push({ type: 'discard', seat, card: action.card });
    state.locked = null;
    if (state.stock.length <= 2 || state.turnsThisHand >= state.config.maxTurns) {
      drawnHand(state, events);
      return events;
    }
    state.phase = 'DRAW';
    state.turn = 1 - seat;
    return events;
  },

  getActors(state) {
    return state.phase === 'FIRST' || state.phase === 'DRAW' || state.phase === 'DISCARD' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    const none: GinLegal = { canTakeUpcard: false, canPassUpcard: false, canDrawStock: false, canDrawDiscard: false, discardable: [], knockable: [] };
    if (seat !== state.turn) return none;
    if (state.phase === 'FIRST') return { ...none, canTakeUpcard: true, canPassUpcard: true };
    if (state.phase === 'DRAW') return { ...none, canDrawStock: true, canDrawDiscard: !state.stockOnly && state.discard.length > 0 };
    if (state.phase === 'DISCARD') {
      const hand = state.hands[seat]!;
      return { ...none, discardable: hand.filter((c) => c !== state.locked), knockable: knockableCards(hand, state.locked, state.config.knockMax) };
    }
    return none;
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'FIRST') return { type: 'passUpcard' };
    if (state.phase === 'DRAW') return { type: 'draw', from: 'stock' };
    return { type: 'discard', card: ginBestDiscard(state.hands[seat]!, state.locked) };
  },

  getView(state, seat) {
    return {
      gameType: 'GIN_RUMMY',
      config: state.config,
      seat,
      phase: state.phase,
      hand: state.hand,
      dealer: state.dealer,
      turn: state.turn,
      cards: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      stockCount: state.stock.length,
      discardTop: state.discard.at(-1) ?? null,
      discardPile: [...state.discard],
      locked: state.locked,
      picked: state.picked.map((p) => [...p]),
      scores: [...state.scores],
      handsWon: [...state.handsWon],
      handResults: state.handResults.map((r) => ({ ...r, melds: r.melds.map((m) => m.map((x) => ({ kind: x.kind, cards: [...x.cards] }))), deadwood: r.deadwood.map((d) => [...d]), laidOff: [...r.laidOff] })),
      totals: state.finalScores ? [...state.finalScores] : [...state.scores],
    };
  },

  getSystemStep(state) {
    if (state.phase === 'HAND_END') return { kind: 'nextRound', delayMs: state.config.handEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'HAND_END') {
      state.hand++;
      state.dealer = (state.dealer + 1) % 2;
      deal(state, rng, events);
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standingsFromScores((state.finalScores ?? state.scores).map((score, seat) => ({ seat, score })));
  },

  getRoundsCompleted: (state) => state.handResults.length,
};

